import { useEffect, useRef, useState } from 'react'
import { Dropdown } from 'shared/components/Dropdown/Dropdown'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { usePlacesAutoCompleteOptions } from 'shared/hooks/usePlacesAutoCompleteOptions'
import { object, string } from 'yup'

import { getDropdownStates } from '../../helpers/states'
import { Address as AddressWithId } from '../../models/address'
import { AutoComplete } from '../AutoComplete/AutoComplete'
import { AddressContainer, CityState, ErrorMessage } from './AddressFields.styles'

type PlaceType = google.maps.places.AutocompletePrediction

export type Address = Omit<AddressWithId, 'id' | 'country'>

// all props are from useForm
type AddressFieldsProps = {
  errors: Address
  errorMessage?: string
  value: Address
  onChange: (value: Address) => void
  shouldHideHeader?: boolean
  backgroundColor?: string
  noMargin?: boolean
}

export const AddressFields = ({
  errors,
  value,
  backgroundColor,
  errorMessage,
  noMargin,
  onChange,
}: AddressFieldsProps) => {
  const googlePlacesElement = useRef<HTMLDivElement>(null)
  const [line1, setLine1] = useState(value.line1)
  const [line2, setLine2] = useState(value.line2)
  const [city, setCity] = useState(value.city)
  const [state, setState] = useState(value.state)
  const [zip, setZip] = useState(value.zip)
  const { options, placesService } = usePlacesAutoCompleteOptions(googlePlacesElement.current, line1)

  const addressAutocomplete = (address: any) => {
    setCity(address.city)
    setState(address.state)
    setZipCode(address.place)
  }

  const setZipCode = (place: PlaceType) => {
    const request = { placeId: place.place_id, fields: ['address_component'] }

    placesService?.getDetails(request, (result, status) => {
      if (status === (window as any).google.maps.places.PlacesServiceStatus.OK) {
        if (result && result?.address_components && result?.address_components?.length > 0) {
          const postCodeInfo = result.address_components.find(
            (component) => component.types[0] === 'postal_code'
          )

          const zipCode = postCodeInfo?.short_name ?? ''
          setZip(zipCode)
        }
      }
    })
  }

  useEffect(() => {
    onChange({
      line1,
      line2,
      city,
      state,
      zip,
    })
    // Ignore onChange
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line1, line2, city, state, zip])

  return (
    <AddressContainer $noMargin={noMargin}>
      {/* this div is needed to initialize PlacesService*/}
      <div ref={googlePlacesElement}></div>
      <AutoComplete
        value={line1}
        name="line1"
        onChange={(val) => (val || val === '') && setLine1(val)}
        placeholder="Street 1"
        options={options}
        optionSelect={addressAutocomplete}
        error={Boolean(errors.line1)}
        backgroundColor={backgroundColor}
      />

      <TextInput
        value={line2}
        name="line2"
        onChange={(e) => setLine2(e.target.value)}
        placeholder="Street 2 (Optional)"
        backgroundColor={backgroundColor}
      />

      <CityState>
        <TextInput
          value={city}
          name="city"
          onChange={(e) => setCity(e.target.value)}
          placeholder="City"
          error={Boolean(errors.city)}
          backgroundColor={backgroundColor}
        />

        <Dropdown
          value={state}
          onChange={setState}
          options={getDropdownStates()}
          label="State"
          dataTestId="stateDropdown"
          backgroundColor={backgroundColor}
          error={Boolean(errors.state)}
        />

        <TextInput
          value={zip}
          name="zip"
          onChange={(e) => setZip(e.target.value)}
          placeholder="Zip"
          error={Boolean(errors.zip)}
          backgroundColor={backgroundColor}
        />
      </CityState>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </AddressContainer>
  )
}

const postBoxTest = (input: string | undefined) =>
  RegExp('\\bp(o|ost)*\\.*\\s*[o|0]*(ffice)*\\.*\\s*\\b', 'i').test(input || '')

const POST_BOX_LINE1_ERROR = 'P.O. Box is not accepted in line1.'
const POST_BOX_LINE2_ERROR = 'P.O. Box is not accepted in line2'

export const AddressSchema = () =>
  object().shape({
    line1: string()
      .test('line1-valid', POST_BOX_LINE1_ERROR, (val) => !postBoxTest(val))
      .required(),
    line2: string()
      .test('line2-valid', POST_BOX_LINE2_ERROR, (val) => !postBoxTest(val))
      .notRequired(),
    city: string().required(),
    state: string().required(),
    zip: string().matches(RegExp('\\d{5}|\\d{5}-\\d{4}'), 'Invalid zip code').required(),
  })

export function formatErrors(errors: any) {
  const formattedErrors = {
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
  }

  Object.keys(errors || {}).forEach((k) => {
    if (errors[k]?.message) {
      // @ts-ignore
      formattedErrors[k] = errors[k]?.message
    }
  })

  return formattedErrors
}

export const emptyAddress: Address = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  zip: '',
}

export function isSameAddress(address1: Address, address2: Address) {
  return (
    address1.line1 === address2.line1 &&
    address1.line2 === address2.line2 &&
    address1.city === address2.city &&
    address1.state === address2.state &&
    address1.zip === address2.zip
  )
}
